/**
 * ManageBookingPage — public, tokenised page a client lands on from the
 * "Reschedule / Cancel" magic link inside their confirmation email.
 * No auth required — validates via the token in the URL.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, XCircle, CheckCircle2, Calendar, Clock, AlertTriangle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ManageBookingPage() {
  const { tenantId, bookingId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('t') || '';
  const initialAction = params.get('a') || 'cancel';

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(initialAction);       // 'cancel' | 'reschedule'
  const [reason, setReason] = useState('');
  const [newStart, setNewStart] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [done, setDone] = useState(null);                // { kind, message }

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(
          `${API}/api/booking/public/manage/${tenantId}/${bookingId}`,
          { params: { t: token, a: initialAction } }
        );
        setData(d);
      } catch (e) {
        setError(e?.response?.data?.detail || 'This link is invalid or has already been used.');
      } finally {
        setLoading(false);
      }
    })();
  }, [tenantId, bookingId, token, initialAction]);

  const when = useMemo(() => {
    if (!data?.booking?.start_at) return '';
    try { return new Date(data.booking.start_at).toLocaleString(undefined, { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
    catch { return data.booking.start_at; }
  }, [data]);

  const submitCancel = async () => {
    setSubmitting(true);
    try {
      const { data: res } = await axios.post(
        `${API}/api/booking/public/manage/${tenantId}/${bookingId}/cancel`,
        { t: token, reason },
      );
      setDone({
        kind: 'cancelled',
        message: res.late_cancel_fee_applied > 0
          ? `Your booking is cancelled. A late-cancellation fee of ${res.late_cancel_fee_applied} was applied per the business's policy.`
          : 'Your booking is cancelled. We hope to see you again soon.',
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cancellation failed');
    } finally {
      setSubmitting(false);
      setConfirmOpen(false);
    }
  };

  const submitReschedule = async () => {
    if (!newStart) { toast.error('Pick a new date/time first'); return; }
    setSubmitting(true);
    try {
      const isoStart = new Date(newStart).toISOString();
      const { data: res } = await axios.post(
        `${API}/api/booking/public/manage/${tenantId}/${bookingId}/reschedule`,
        { t: token, start_at: isoStart },
      );
      setDone({
        kind: 'rescheduled',
        message: `You're all set — new time: ${new Date(res.new_start_at).toLocaleString()}. A fresh confirmation is on its way.`,
      });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === 'object' && detail?.error === 'slot_full') {
        toast.error('That slot just filled up. Try another time.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Reschedule failed');
      }
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <XCircle className="h-12 w-12 text-rose-500 mx-auto mb-3" />
            <h1 className="text-xl font-semibold">Link no longer valid</h1>
            <p className="text-sm text-slate-600 mt-2">{typeof error === 'string' ? error : 'Please contact the business directly.'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
            <h1 className="text-xl font-semibold">{done.kind === 'cancelled' ? 'Cancelled' : 'Rescheduled'}</h1>
            <p className="text-sm text-slate-600 mt-2">{done.message}</p>
            <Button className="mt-6" onClick={() => navigate(`/book/${tenantId}`)} data-testid="book-again-btn">Book again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const b = data.booking;
  const isLate = data.cancel_is_late;

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-lg mx-auto space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Manage your booking</CardTitle>
            <p className="text-xs text-slate-500">{data.business_name}</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg bg-slate-50 border p-3 text-sm">
              <div className="flex items-center gap-2 text-slate-700"><Calendar className="h-4 w-4" /> {b.service_name}</div>
              <div className="flex items-center gap-2 text-slate-700 mt-1"><Clock className="h-4 w-4" /> {when}</div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Button
                variant={mode === 'reschedule' ? 'default' : 'outline'}
                onClick={() => setMode('reschedule')}
                disabled={!data.can_reschedule}
                data-testid="mode-reschedule"
              >
                Reschedule
              </Button>
              <Button
                variant={mode === 'cancel' ? 'default' : 'outline'}
                onClick={() => setMode('cancel')}
                disabled={!data.can_cancel}
                className={mode === 'cancel' ? 'bg-rose-600 hover:bg-rose-700' : ''}
                data-testid="mode-cancel"
              >
                Cancel
              </Button>
            </div>

            {mode === 'reschedule' && data.can_reschedule && (
              <div className="space-y-2">
                <Label htmlFor="new-start" className="text-xs">Pick a new date & time</Label>
                <Input
                  id="new-start"
                  type="datetime-local"
                  value={newStart}
                  onChange={(e) => setNewStart(e.target.value)}
                  data-testid="new-start-input"
                />
                <p className="text-[11px] text-slate-500">
                  Must be at least {data.policy.reschedule_window_hours}h from now.
                </p>
                <Button onClick={submitReschedule} disabled={submitting || !newStart} className="w-full" data-testid="submit-reschedule">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm new time'}
                </Button>
              </div>
            )}

            {mode === 'reschedule' && !data.can_reschedule && (
              <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded p-3 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>Reschedule window has passed ({data.policy.reschedule_window_hours}h before start). Please contact {data.business_name} directly.</span>
              </div>
            )}

            {mode === 'cancel' && data.can_cancel && (
              <div className="space-y-2">
                {isLate && (
                  <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded p-3 flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>Cancelling now is inside the {data.policy.cancel_window_hours}h notice window. A late-cancel fee of {data.policy.late_cancel_fee_pct}% may apply per business policy.</span>
                  </div>
                )}
                <Label htmlFor="reason" className="text-xs">Reason (optional)</Label>
                <Textarea id="reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cancel-reason-input" />
                <Button variant="destructive" onClick={() => setConfirmOpen(true)} disabled={submitting} className="w-full" data-testid="submit-cancel">
                  Cancel my booking
                </Button>
              </div>
            )}

            {mode === 'cancel' && !data.can_cancel && (
              <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded p-3">
                Self-cancel is not available for this booking.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this booking?</AlertDialogTitle>
            <AlertDialogDescription>
              This will free up your slot. {isLate ? `A late-cancel fee of ${data.policy.late_cancel_fee_pct}% of the service price may be applied.` : 'No cancellation fee applies.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep booking</AlertDialogCancel>
            <AlertDialogAction onClick={submitCancel} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-cancel-btn">
              Yes, cancel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
